import { dateKeyInMoldova } from "./catalog";
import type { ProductFilters, ProductView } from "./types";

export interface DiscoveryFeedOptions {
  personalStores?: readonly string[];
  personalCategoryIds?: readonly string[];
  today?: string;
  slateSize?: number;
  /** Stable for one open Mini App; omitted for the deterministic baseline. */
  visitSeed?: number;
  previousTopKeys?: readonly string[];
  /** Newest first; public offer keys selected for recent home-feed visits. */
  recentVisits?: readonly (readonly string[])[];
}

const DEFAULT_SLATE_SIZE = 48;
const FIRST_PAGE_SIZE = 24;
const INTRO_QUALITY_GAP = 18;
const DISCOVERY_TEMPERATURE = 12;

export function discoveryFeedContext(filters: ProductFilters): boolean {
  return (
    !filters.query.trim() &&
    filters.store === "all" &&
    filters.catalogId === "all" &&
    filters.categoryId === "all" &&
    filters.sort === "default"
  );
}

export function discoveryOfferKey(product: ProductView): string {
  return `${product.catalog_id}:${product.id}`;
}

const CATEGORY_PRIOR: Readonly<Record<string, number>> = {
  produce: 30,
  dairy_eggs: 17,
  meat_deli: 12,
  pantry: 11,
  household_cleaning: 9,
  fish_seafood: 8,
  frozen_ready: 7,
  bakery: 5,
  baby: 3,
  pets: 3,
  soft_drinks: 2,
  sweets_snacks: 2,
  beauty_hygiene: 1,
  home_nonfood: 0,
  other: 0,
};

const GROUP_PRIOR: Readonly<Record<string, number>> = {
  dairy_milk: 40,
  dairy_chicken_eggs: 36,
  bakery_bread: 42,
  produce_potatoes: 38,
  produce_tomatoes: 33,
  produce_apples: 28,
  produce_bananas: 27,
  produce_cucumbers: 28,
  produce_onions: 27,
  produce_carrots: 27,
  produce_cabbage: 26,
  produce_grapes: 25,
  produce_plums: 24,
  produce_peaches: 24,
  produce_watermelon: 26,
  produce_peppers: 25,
  pantry_sunflower_oil: 27,
  meat_chicken_thighs: 31,
  meat_chicken_breast: 31,
  meat_chicken_drumsticks: 30,
  dairy_butter: 28,
  dairy_cottage_cheese: 26,
  pantry_pasta: 25,
  pantry_rice: 25,
  pantry_wheat_flour: 25,
  dairy_sour_cream: 24,
  dairy_kefir: 24,
  dairy_yogurt: 22,
  meat_minced: 22,
  meat_minced_pork: 22,
  drinks_water_still: 21,
  drinks_water: 20,
  dairy_hard_cheese: 18,
  household_laundry_powder: 17,
  household_laundry_liquid: 17,
  household_dish_liquid: 16,
  household_toilet_cleaner: 14,
};

interface GroupNameGate {
  include: RegExp;
  exclude?: RegExp;
}

const GROUP_NAME_GATES: Readonly<Record<string, GroupNameGate>> = {
  dairy_milk: {
    include: /(?:\blapte\b|молок)/,
    exclude:
      /(?:cafea|coffee|кофе|condens|сгущ|\bpraf\b|сух[^\n]*молок|ciocol|шокол|desert|десерт|crema|сливк)/,
  },
  dairy_chicken_eggs: {
    include: /(?:\boua\b|яйц)/,
    exclude: /(?:prepel|перепел|ciocol|шокол|surpriz|сюрприз)/,
  },
  bakery_bread: {
    include: /(?:\bpaine\b|хлеб)/,
    exclude: /(?:pesmet|сухар|crutoane|гренк)/,
  },
  produce_potatoes: {
    include: /(?:cartof|картоф)/,
    exclude: /(?:chips|чипс|congelat|заморож|piure|пюре|amidon|крахмал)/,
  },
  produce_tomatoes: {
    include: /(?:rosii|tomate|помидор|томат)/,
    exclude: /(?:conserv|консерв|pasta|паст|suc|сок|ketchup|кетчуп)/,
  },
  produce_apples: { include: /(?:\bmere\b|яблок)/, exclude: /(?:suc|сок|piure|пюре|uscat|сушен)/ },
  produce_bananas: { include: /(?:banan|банан)/, exclude: /(?:chips|чипс|uscat|сушен)/ },
  produce_cucumbers: { include: /(?:castrav|огур)/, exclude: /(?:murat|марин|conserv|консерв|солен)/ },
  produce_onions: { include: /(?:\bceapa\b|(?:^|\s)лук(?:\s|,|$))/, exclude: /(?:praz|порей|murat|марин)/ },
  produce_carrots: { include: /(?:morcov|морков)/, exclude: /(?:salat|салат|coreean|корейск|suc|сок)/ },
  produce_cabbage: { include: /(?:varza|капуст)/, exclude: /(?:murat|квашен|salat|салат)/ },
  produce_grapes: { include: /(?:strugur|виноград)/, exclude: /(?:suc|сок|uscat|сушен|stafid|изюм)/ },
  produce_plums: { include: /(?:prune|слив[аы])/, exclude: /(?:uscat|сушен|gem|джем)/ },
  produce_peaches: { include: /(?:piersic|nectarin|персик|нектарин)/, exclude: /(?:conserv|консерв|suc|сок)/ },
  produce_watermelon: { include: /(?:pepene|pepeni|арбуз|дын)/ },
  produce_peppers: { include: /(?:ardei|перец)/, exclude: /(?:murat|марин|conserv|консерв|macinat|молот|iut|chili|чили|остр)/ },
  pantry_sunflower_oil: {
    include: /(?:ulei[^\n]*floarea[^\n]*soarelui|подсолнеч[^\n]*масл)/,
  },
  meat_chicken_thighs: {
    include: /(?:pulpe?[^\n]*(?:pui|gaina)|sold[^\n]*(?:pui|gaina)|бедр|окороч)/,
    exclude: /(?:marinat|condimentat|\bin sos\b|марин|приправ|в соус)/,
  },
  meat_chicken_breast: {
    include: /(?:piept[^\n]*(?:pui|gaina)|file[^\n]*pui|курин[^\n]*(?:груд|филе)|грудк[^\n]*кур)/,
  },
  meat_chicken_drumsticks: {
    include: /(?:ciocanele?[^\n]*(?:pui|gaina)|gambe?[^\n]*(?:pui|gaina)|голен[^\n]*кур|курин[^\n]*голен)/,
  },
  dairy_butter: {
    include: /(?:\bunt\b|масл[^\n]*сливоч|сливоч[^\n]*масл)/,
    exclude: /(?:arahide|peanut|арахис|croissant|круассан|biscui|печень)/,
  },
  dairy_cottage_cheese: {
    include: /(?:branza[^\n]*(?:vaci|proaspata)|творог)/,
  },
  pantry_pasta: {
    include: /(?:paste[^\n]*(?:fainoase|alimentare)|macaro|spaghet|вермишел|макарон|спагет)/,
    exclude: /(?:tomat|томат)/,
  },
  pantry_rice: { include: /(?:\borez\b|рис)/ },
  pantry_wheat_flour: { include: /(?:\bfaina\b|мук)/ },
  dairy_sour_cream: { include: /(?:smantana|сметан)/, exclude: /(?:branza|сыр|sos|соус)/ },
  dairy_kefir: { include: /(?:chefir|kefir|кефир)/ },
  dairy_yogurt: { include: /(?:iaurt|йогурт)/ },
  meat_minced: { include: /(?:carne[^\n]*tocata|фарш)/ },
  meat_minced_pork: { include: /(?:porc[^\n]*tocat|фарш[^\n]*свин)/ },
  drinks_water_still: { include: /(?:\bapa\b|вод)/ },
  drinks_water: { include: /(?:\bapa\b|вод)/ },
  dairy_hard_cheese: { include: /(?:cascaval|branza[^\n]*tare|сыр)/ },
  household_laundry_powder: {
    include: /(?:detergent[^\n]*(?:pudra|rufe)|порош[^\n]*стир)/,
  },
  household_laundry_liquid: {
    include: /(?:detergent[^\n]*(?:lichid|rufe)|гел[^\n]*стир|средств[^\n]*стир)/,
  },
  household_dish_liquid: {
    include: /(?:vase|мыть[^\n]*посуд|посуд)/,
  },
  household_toilet_cleaner: {
    include: /(?:toaleta|wc|унитаз|туалет)/,
  },
};

const FIRST_EIGHT_FOOD_CATEGORIES = new Set([
  "produce",
  "dairy_eggs",
  "meat_deli",
  "pantry",
  "bakery",
  "fish_seafood",
  "frozen_ready",
]);

const FIRST_TWELVE_CATEGORY_LIMITS: Readonly<Record<string, number>> = {
  household_cleaning: 1,
  soft_drinks: 1,
  sweets_snacks: 0,
  beauty_hygiene: 0,
  home_nonfood: 0,
  baby: 0,
  pets: 0,
  other: 0,
};

const FIRST_PAGE_CATEGORY_LIMITS: Readonly<Record<string, number>> = {
  household_cleaning: 2,
  soft_drinks: 2,
  sweets_snacks: 1,
  beauty_hygiene: 1,
  home_nonfood: 1,
  baby: 1,
  pets: 1,
  other: 1,
};

const CORE_MEAT_GROUPS = new Set([
  "meat_chicken_thighs",
  "meat_chicken_breast",
  "meat_chicken_drumsticks",
  "meat_minced",
  "meat_minced_pork",
  "meat_pork_pulp",
]);
const CORE_STAPLE_GROUPS = new Set([
  "bakery_bread",
  "pantry_sunflower_oil",
  "pantry_pasta",
  "pantry_rice",
  "pantry_wheat_flour",
]);
const CORE_DAIRY_GROUPS = new Set([
  "dairy_milk",
  "dairy_chicken_eggs",
  "dairy_butter",
  "dairy_cottage_cheese",
  "dairy_sour_cream",
  "dairy_kefir",
  "dairy_yogurt",
  "dairy_hard_cheese",
]);

type CoreSegment = "produce" | "dairy" | "meat" | "staples";

interface ScoredProduct {
  product: ProductView;
  score: number;
  originalIndex: number;
  category: string;
  store: string;
  group: string | null;
  identity: string;
  coreSegment: CoreSegment | null;
  priorityGroup: string | null;
  featuredFood: boolean;
  apparel: boolean;
}

type UnitPriceBaselines = ReadonlyMap<string, number>;

function validPrice(product: ProductView): boolean {
  return (
    product.new_price_mdl !== null &&
    Number.isFinite(product.new_price_mdl) &&
    product.new_price_mdl > 0
  );
}

function effectiveDiscount(product: ProductView): number {
  const oldPrice = product.old_price_mdl;
  const newPrice = product.new_price_mdl;
  if (
    oldPrice !== null &&
    newPrice !== null &&
    Number.isFinite(oldPrice) &&
    Number.isFinite(newPrice) &&
    oldPrice > 0 &&
    newPrice >= 0 &&
    newPrice < oldPrice
  ) {
    return ((oldPrice - newPrice) / oldPrice) * 100;
  }
  const declared = product.discount_percent;
  return declared !== null &&
    Number.isFinite(declared) &&
    declared >= 0 &&
    declared <= 100
    ? declared
    : 0;
}

function purchaseOutlayScore(product: ProductView): number {
  const price = product.new_price_mdl;
  if (price === null || !Number.isFinite(price) || price <= 0) {
    return 0;
  }
  if (price <= 50) {
    return 3;
  }
  if (price <= 100) {
    return 1;
  }
  if (price > 250) {
    return -2;
  }
  return 0;
}

function unitPriceKey(group: string, product: ProductView): string | null {
  const unit = product.unit_price;
  return unit &&
    !unit.is_range &&
    Number.isFinite(unit.minimum_mdl) &&
    unit.minimum_mdl > 0
    ? `${group}:${unit.basis}`
    : null;
}

function buildUnitPriceBaselines(
  products: readonly ProductView[],
): UnitPriceBaselines {
  const values = new Map<string, number[]>();
  for (const product of products) {
    const group = validatedPriorityGroup(product);
    if (!group) {
      continue;
    }
    const key = unitPriceKey(group, product);
    if (!key) {
      continue;
    }
    const prices = values.get(key) ?? [];
    prices.push(product.unit_price!.minimum_mdl);
    values.set(key, prices);
  }

  const baselines = new Map<string, number>();
  for (const [key, prices] of values) {
    prices.sort((left, right) => left - right);
    const index = prices.length >= 5 ? 1 : 0;
    baselines.set(key, prices[index]!);
  }
  return baselines;
}

function relativeUnitValueScore(
  product: ProductView,
  priorityGroup: string | null,
  baselines: UnitPriceBaselines,
): number {
  if (!priorityGroup) {
    return 0;
  }
  const key = unitPriceKey(priorityGroup, product);
  if (!key) {
    return 0;
  }
  const baseline = baselines.get(key);
  if (!baseline || baseline <= 0) {
    return 0;
  }
  const ratio = product.unit_price!.minimum_mdl / baseline;
  if (ratio <= 1.05) {
    return 10;
  }
  if (ratio <= 1.15) {
    return 7;
  }
  if (ratio <= 1.3) {
    return 4;
  }
  if (ratio <= 1.5) {
    return 1;
  }
  if (ratio > 2) {
    return -5;
  }
  return 0;
}

function isoDayNumber(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(timestamp)
    ? Math.floor(timestamp / (24 * 60 * 60 * 1_000))
    : null;
}

function freshnessScore(product: ProductView, today: string): number {
  const todayDay = isoDayNumber(today);
  const startDay = isoDayNumber(product.catalog.valid_from);
  if (todayDay === null || startDay === null) {
    return 0;
  }
  const age = todayDay - startDay;
  if (age <= 1) {
    return 4;
  }
  if (age <= 4) {
    return 2;
  }
  return 0;
}

function normalizedProductText(product: ProductView): string {
  // Notes can mention other ingredients; rank the product, not its serving suggestion.
  return [product.product_ro, product.product_ru]
    .filter(Boolean)
    .join("\n")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("ro");
}

// Titles only: a detergent's usage notes may legitimately mention clothing.
function isApparel(product: ProductView): boolean {
  const title = [product.product_ro, product.product_ru].join(" ")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("ro");
  return /(?:леггинс|легинс|лосин|колгот|кепк|бейсболк|шапк|панамк|футболк|носк[иов]|трус[ыов]|бюстгальтер|купальник|\b(?:colanti|sapc[ai]|caciul[ai]|tricou|tricouri|sosete|chiloti|sutien)\b)/.test(title);
}

function validatedPriorityGroup(product: ProductView): string | null {
  const group = product.comparison_group_id;
  const text = normalizedProductText(product);
  if (group && GROUP_PRIOR[group] !== undefined) {
    const gate = GROUP_NAME_GATES[group];
    return gate && gate.include.test(text) && !gate.exclude?.test(text) ? group : null;
  }
  // Many fresh vegetables have no comparison tag yet. Recognize a narrow set
  // for ranking only; do not alter the product or manufacture comparison data.
  if (!group && product.category_id === "produce" && !isApparel(product)) {
    for (const [family, gate] of Object.entries(GROUP_NAME_GATES)) {
      if (family.startsWith("produce_") && gate.include.test(text) && !gate.exclude?.test(text)) {
        return family;
      }
    }
  }
  return null;
}

/** The same semantic family used by the selector, also exposed to local audits.
 * An untagged potato is still a potato, not a new family for every offer ID.
 */
export function discoveryDiversityFamily(product: ProductView): string {
  return (isApparel(product) ? null : validatedPriorityGroup(product)) ??
    product.comparison_group_id ?? (product.category_id || "other");
}

function coreSegment(
  product: ProductView,
  priorityGroup: string | null,
): CoreSegment | null {
  const category = product.category_id || "other";
  if (category === "produce" && priorityGroup?.startsWith("produce_")) {
    return "produce";
  }
  if (priorityGroup && CORE_DAIRY_GROUPS.has(priorityGroup)) {
    return "dairy";
  }
  if (priorityGroup && CORE_MEAT_GROUPS.has(priorityGroup)) {
    return "meat";
  }
  if (priorityGroup && CORE_STAPLE_GROUPS.has(priorityGroup)) {
    return "staples";
  }
  return null;
}

function productIdentity(product: ProductView): string {
  return product.product_id || `${product.catalog_id}:${product.id}`;
}

function scoreProduct(
  product: ProductView,
  originalIndex: number,
  personalStores: ReadonlySet<string>,
  personalCategoryIds: ReadonlySet<string>,
  unitPriceBaselines: UnitPriceBaselines,
  today: string,
): ScoredProduct {
  const category = product.category_id || "other";
  const apparel = isApparel(product);
  const priorityGroup = apparel ? null : validatedPriorityGroup(product);
  const group = priorityGroup ?? product.comparison_group_id ?? null;
  const discount = Math.min(45, Math.max(0, effectiveDiscount(product)));
  let score = CATEGORY_PRIOR[category] ?? 0;
  score += priorityGroup ? GROUP_PRIOR[priorityGroup] ?? 0 : 0;
  score += discount * 0.42;
  score += validPrice(product) ? 10 : -80;
  score += purchaseOutlayScore(product);
  score += product.unit_price && !product.unit_price.is_range ? 5 : 0;
  score += relativeUnitValueScore(product, priorityGroup, unitPriceBaselines);
  score += group ? 2 : 0;
  score += freshnessScore(product, today);
  score += personalStores.has(product.catalog.chain) ? 8 : 0;
  score += personalCategoryIds.has(category) ? 4 : 0;
  score -= apparel ? 40 : 0;

  return {
    product,
    score,
    originalIndex,
    category,
    store: product.catalog.chain,
    group,
    identity: productIdentity(product),
    coreSegment: apparel ? null : coreSegment(product, priorityGroup),
    priorityGroup,
    featuredFood: FIRST_EIGHT_FOOD_CATEGORIES.has(category) && !apparel,
    apparel,
  };
}

function increment(counts: Map<string, number>, key: string): void {
  counts.set(key, (counts.get(key) ?? 0) + 1);
}

function adjustedScore(
  candidate: ScoredProduct,
  selected: readonly ScoredProduct[],
  categoryCounts: ReadonlyMap<string, number>,
  storeCounts: ReadonlyMap<string, number>,
  groupCounts: ReadonlyMap<string, number>,
  identityCounts: ReadonlyMap<string, number>,
): number {
  const categoryCount = categoryCounts.get(candidate.category) ?? 0;
  const storeCount = storeCounts.get(candidate.store) ?? 0;
  const groupCount = candidate.group
    ? groupCounts.get(candidate.group) ?? 0
    : 0;
  const identityCount = identityCounts.get(candidate.identity) ?? 0;
  const previous = selected.at(-1);
  const previousTwo = selected.at(-2);

  return (
    candidate.score -
    categoryCount * 5 -
    storeCount * 3.5 -
    groupCount * 11 -
    identityCount * 30 -
    (previous?.category === candidate.category ? 6 : 0) -
    (previous?.store === candidate.store ? 5 : 0) -
    (previous?.store === candidate.store &&
    previousTwo?.store === candidate.store
      ? 16
      : 0)
  );
}

function withinSlateConstraints(
  candidate: ScoredProduct,
  position: number,
  categoryCounts: ReadonlyMap<string, number>,
  storeCounts: ReadonlyMap<string, number>,
  groupCounts: ReadonlyMap<string, number>,
  segmentCounts: ReadonlyMap<string, number>,
): boolean {
  const categoryCount = categoryCounts.get(candidate.category) ?? 0;
  const storeCount = storeCounts.get(candidate.store) ?? 0;
  const groupCount = candidate.group
    ? groupCounts.get(candidate.group) ?? 0
    : 0;

  if (position < 4) {
    return (
      candidate.coreSegment !== null &&
      (segmentCounts.get(candidate.coreSegment) ?? 0) === 0 &&
      storeCount < 2 && groupCount < 1
    );
  }
  if (position < 8) {
    return (
      FIRST_EIGHT_FOOD_CATEGORIES.has(candidate.category) &&
      categoryCount < 2 &&
      storeCount < 2 &&
      groupCount < 1
    );
  }
  if (position < 12) {
    const categoryLimit = FIRST_TWELVE_CATEGORY_LIMITS[candidate.category] ?? 3;
    return (
      categoryCount < categoryLimit &&
      storeCount < 3 &&
      groupCount < 1
    );
  }
  if (position < FIRST_PAGE_SIZE) {
    const categoryLimit = FIRST_PAGE_CATEGORY_LIMITS[candidate.category] ?? 4;
    return (
      categoryCount < categoryLimit &&
      storeCount < 4 &&
      groupCount < 2
    );
  }
  return true;
}

function bestCandidateIndex(
  candidates: readonly ScoredProduct[],
  selected: readonly ScoredProduct[],
  categoryCounts: ReadonlyMap<string, number>,
  storeCounts: ReadonlyMap<string, number>,
  groupCounts: ReadonlyMap<string, number>,
  identityCounts: ReadonlyMap<string, number>,
  segmentCounts: ReadonlyMap<string, number>,
  visit?: DiscoverySelection,
): number {
  const position = selected.length;
  let bestIndex = -1;
  let bestScore = Number.NEGATIVE_INFINITY;

  // Relax variety before relaxing relevance. A small/single-store selection
  // must not promote a cap simply because a fourth core segment is missing.
  for (let pass = 0; pass < (visit ? 4 : 3); pass += 1) {
    const pool: SelectionCandidate[] = [];
    for (let index = 0; index < candidates.length; index += 1) {
      const candidate = candidates[index]!;
      if (pass < (visit ? 3 : 2) && (
        (position < 8 && !candidate.featuredFood) ||
        (position < FIRST_PAGE_SIZE && candidate.apparel)
      )) {
        continue;
      }
      if (visit && pass < 2 && position < 8 && (
        !visit.introEligible.has(candidate) || (identityCounts.get(candidate.identity) ?? 0) > 0
      )) continue;
      if (
        pass === 0 &&
        !withinSlateConstraints(
          candidate,
          position,
          categoryCounts,
          storeCounts,
          groupCounts,
          segmentCounts,
        )
      ) {
        continue;
      }
      const score = adjustedScore(
        candidate,
        selected,
        categoryCounts,
        storeCounts,
        groupCounts,
        identityCounts,
      );
      if (visit) {
        // Strong everyday priors still decide admission; don't let the exact
        // prior for milk/potatoes monopolize every new first position.
        const prior = candidate.priorityGroup ? GROUP_PRIOR[candidate.priorityGroup] ?? 0 : 0;
        pool.push({ index, item: candidate, utility: score - Math.max(0, prior - 28) });
        continue;
      }
      if (
        score > bestScore ||
        (score === bestScore &&
          candidate.originalIndex <
            (candidates[bestIndex]?.originalIndex ?? Number.MAX_SAFE_INTEGER))
      ) {
        bestIndex = index;
        bestScore = score;
      }
    }
    if (visit && pool.length) return diverseCandidateIndex(pool, position, visit);
    if (bestIndex >= 0) {
      return bestIndex;
    }
  }
  return 0;
}

function selectionHash(seed: number, text: string): number {
  let hash = (seed >>> 0) ^ 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash = Math.imul(hash ^ text.charCodeAt(index), 16777619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d);
  hash ^= hash >>> 15;
  return hash >>> 0;
}

interface SelectionCandidate {
  index: number;
  item: ScoredProduct;
  utility: number;
}

interface DiscoverySelection {
  introEligible: ReadonlySet<ScoredProduct>;
  noise: ReadonlyMap<ScoredProduct, number>;
  fatigue: ReadonlyMap<ScoredProduct, number>;
  previousIdentities: ReadonlySet<string>;
  previousHeadFamilies: ReadonlySet<string>;
}

function selectionFamily(item: ScoredProduct): string {
  return item.group ?? item.category;
}

function everydayIntroFood(item: ScoredProduct): boolean {
  if (item.priorityGroup !== null) return true;
  const title = normalizedProductText(item.product);
  if (item.category === "fish_seafood") {
    return /(?:минтай|хек|скумбр|сельд|сельдь|карп|лосос|somon|merluc|macrou|hering|pollock|mintai|\bcrap\b)/.test(title) &&
      !/(?:икр|icre|caviar|суп|supa|паштет|pate|салат|salat)/.test(title);
  }
  return item.category === "frozen_ready" && /(?:пельмен|вареник|pelmeni|coltunasi)/.test(title);
}

function buildDiscoverySelection(
  scored: readonly ScoredProduct[],
  seed: number,
  recentVisits: readonly (readonly string[])[],
): DiscoverySelection {
  const byKey = new Map(scored.map((item) => [discoveryOfferKey(item.product), item]));
  const bestByFamily = new Map<string, ScoredProduct>();
  for (const item of scored) {
    if (!validPrice(item.product) || !item.featuredFood) continue;
    const family = selectionFamily(item);
    if (item.score > (bestByFamily.get(family)?.score ?? -Infinity)) bestByFamily.set(family, item);
  }
  const introEligible = new Set<ScoredProduct>();
  const noise = new Map<ScoredProduct, number>();
  const fatigue = new Map<ScoredProduct, number>();
  const visits = recentVisits.slice(0, 6).map((keys) => keys.slice(0, 12).flatMap((key) => byKey.get(key) ?? []));
  const previousIdentities = new Set(visits[0]?.slice(0, 8).map((item) => item.identity));
  const previousHeadFamilies = new Set(visits[0]?.slice(0, 2).map(selectionFamily));

  for (const item of scored) {
    const anchor = bestByFamily.get(selectionFamily(item));
    const price = item.product.new_price_mdl;
    const anchorPrice = anchor?.product.new_price_mdl;
    const unit = item.product.unit_price;
    const anchorUnit = anchor?.product.unit_price;
    // Compare with today's family anchor, never the previous random selection:
    // neither repeated visits nor novelty can ratchet the quality floor down.
    if (anchor && validPrice(item.product) && item.featuredFood &&
      item.score >= anchor.score - INTRO_QUALITY_GAP &&
      price! <= Math.max(anchorPrice! * 1.75, anchorPrice! + 10) &&
      (!unit || !anchorUnit || unit.is_range || anchorUnit.is_range || unit.basis !== anchorUnit.basis ||
        unit.minimum_mdl <= anchorUnit.minimum_mdl * 1.5) &&
      everydayIntroFood(item)
    ) introEligible.add(item);

    // Gumbel perturbation = weighted sampling without replacement, generated
    // once per visit/candidate, not a random comparator or render-time shuffle.
    const uniform = (selectionHash(seed, discoveryOfferKey(item.product)) + .5) / 0x1_0000_0000;
    noise.set(item, -Math.log(-Math.log(uniform)));
    let penalty = 0;
    for (const [age, visit] of visits.entries()) {
      const sameItem = visit.some((old) => old.identity === item.identity);
      const sameFamily = visit.slice(0, 8).some((old) => selectionFamily(old) === selectionFamily(item));
      penalty += ((sameItem ? 24 : 0) + (sameFamily ? 7 : 0)) * 0.55 ** age;
    }
    fatigue.set(item, penalty);
  }
  return { introEligible, noise, fatigue, previousIdentities, previousHeadFamilies };
}

function diverseCandidateIndex(
  candidates: SelectionCandidate[],
  position: number,
  visit: DiscoverySelection,
): number {
  const bestUtility = candidates.reduce((best, candidate) => Math.max(best, candidate.utility), -Infinity);
  // Quality admission precedes recency/randomness. Later pages can explore more
  // categories, but a poor offer never wins solely for being unseen.
  let pool = candidates.filter((candidate) => candidate.utility >= bestUtility - (position < 8 ? 32 : 20));
  if (position < 8) {
    const fresh = pool.filter(({ item }) => !visit.previousIdentities.has(item.identity));
    if (fresh.length) pool = fresh;
  }
  if (position < 2) {
    const differentFamily = pool.filter(({ item }) => !visit.previousHeadFamilies.has(selectionFamily(item)));
    if (differentFamily.length) pool = differentFamily;
  }
  const familySizes = new Map<string, number>();
  for (const { item } of pool) increment(familySizes, selectionFamily(item));
  const temperature = position < 8 ? DISCOVERY_TEMPERATURE : 8;
  let bestIndex = pool[0]!.index;
  let bestOriginalIndex = pool[0]!.item.originalIndex;
  let bestScore = -Infinity;
  for (const { index, item, utility } of pool) {
    // Normalize family mass: 25 yogurts must not get 25 lottery tickets against
    // one potato offer. Diversity is by product role as well as SKU/store.
    const score = utility - visit.fatigue.get(item)! + temperature * (
      visit.noise.get(item)! - Math.log(familySizes.get(selectionFamily(item))!)
    );
    if (score > bestScore || (score === bestScore && item.originalIndex < bestOriginalIndex)) {
      bestIndex = index;
      bestOriginalIndex = item.originalIndex;
      bestScore = score;
    }
  }
  return bestIndex;
}

/**
 * Builds a reproducible discovery slate for the unfiltered home feed.
 *
 * The ranking deliberately separates item utility from slate composition:
 * price/data quality, everyday relevance, discount, freshness and soft user
 * preferences determine the candidate score; category/store/family caps then
 * keep the first screen useful as a set. Seeded selection rebuilds the whole
 * slate with recency/family diversity; no item or family owns a fixed slot.
 * Omitting the seed gives the reproducible greedy baseline. No item is removed.
 */
export function rankDiscoveryFeed(
  products: readonly ProductView[],
  options: DiscoveryFeedOptions = {},
): ProductView[] {
  const personalStores = new Set(options.personalStores ?? []);
  const personalCategoryIds = new Set(options.personalCategoryIds ?? []);
  const today = options.today ?? dateKeyInMoldova(new Date());
  const unitPriceBaselines = buildUnitPriceBaselines(products);
  const slateSize = Math.min(
    products.length,
    Math.max(0, Math.trunc(options.slateSize ?? DEFAULT_SLATE_SIZE)),
  );
  const scored = products.map((product, originalIndex) =>
    scoreProduct(
      product,
      originalIndex,
      personalStores,
      personalCategoryIds,
      unitPriceBaselines,
      today,
    ),
  );
  const priced = scored
    .filter((candidate) => validPrice(candidate.product))
    .sort(
      (left, right) =>
        right.score - left.score || left.originalIndex - right.originalIndex,
    );
  const unpriced = scored
    .filter((candidate) => !validPrice(candidate.product))
    .sort(
      (left, right) =>
        right.score - left.score || left.originalIndex - right.originalIndex,
    );

  const visit = options.visitSeed !== undefined && Number.isFinite(options.visitSeed)
    ? buildDiscoverySelection(scored, options.visitSeed, options.recentVisits ?? [options.previousTopKeys ?? []])
    : undefined;

  const selected: ScoredProduct[] = [];
  const categoryCounts = new Map<string, number>();
  const storeCounts = new Map<string, number>();
  const groupCounts = new Map<string, number>();
  const identityCounts = new Map<string, number>();
  const segmentCounts = new Map<string, number>();

  while (selected.length < slateSize && priced.length > 0) {
    const index = bestCandidateIndex(
      priced,
      selected,
      categoryCounts,
      storeCounts,
      groupCounts,
      identityCounts,
      segmentCounts,
      visit,
    );
    const candidate = priced.splice(index, 1)[0]!;
    selected.push(candidate);
    increment(categoryCounts, candidate.category);
    increment(storeCounts, candidate.store);
    if (candidate.group) {
      increment(groupCounts, candidate.group);
    }
    increment(identityCounts, candidate.identity);
    if (candidate.coreSegment) {
      increment(segmentCounts, candidate.coreSegment);
    }
  }

  const ranked = [
    ...selected,
    ...priced,
    ...unpriced,
  ];
  return ranked.map((candidate) => candidate.product);
}
